import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CurrencyContext = createContext({ currency: 'v', accountDemo: false, signedIn: false, ready: true, busy: false, state: null, refresh: () => {}, registerBusy: () => {} });
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('gv_token')}` });
const gamePath = (path) => /^\/(?:keno\/(?:wallet|bet|config|history|fairness(?:\/rotate|\/reveal\/[^/?]+)?)|blackjack\/(?:config|state|deal|action)|originals\/[^/?]+\/(?:config|state|bet|action|history))(?:\?|$)/.test(path);

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null), [changing, setChanging] = useState(false), [blocked, setBlocked] = useState(false), [error, setError] = useState('');
  const current = useRef({}), locks = useRef(new Map()), pending = useRef(false), fetchCount = useRef(0);
  const state = user?.id && data?.owner === user.id ? data.value : null;
  current.current = { owner: user?.id, state };
  const currency = state?.currency || 'v';
  const registerBusy = useCallback((key, value) => { if (value) locks.current.set(key, true); else locks.current.delete(key); setBlocked(locks.current.size > 0); }, []);
  const refresh = useCallback(async () => {
    if (!user?.id) return;
    const owner = user.id, generation = ++fetchCount.current;
    try {
      const { data: value } = await axios.get(`${API}/account/currency`, { headers: headers() });
      if (current.current.owner === owner && generation === fetchCount.current) { setData({ owner, value }); setError(''); }
    } catch { if (current.current.owner === owner) setError('Currency balances are unavailable. Please retry.'); }
  }, [user?.id]);
  const invalidateRefresh = useCallback(() => { fetchCount.current++; }, []);
  useLayoutEffect(() => {
    const request = axios.interceptors.request.use((config) => {
      const snapshot = current.current;
      if (!snapshot.owner || !config.url?.startsWith(API + '/')) return config;
      const path = config.url.slice(API.length);
      const requested = config.headers?.['X-GV-Currency'];
      const mode = requested || snapshot.state?.currency;
      if (mode === 'demo' && !config.headers?.['X-GV-Real-Wallet'] && gamePath(path)) {
        config.url = API + '/account-demo' + path;
        config.headers = { ...config.headers, ...headers() };
        config.gvDemoOwner = snapshot.owner;
      }
      return config;
    });
    const response = axios.interceptors.response.use((value) => {
      const owner = value.config.gvDemoOwner;
      if (owner && current.current.owner === owner && Number.isSafeInteger(value.data?.balance_minor) && Number.isSafeInteger(value.data?.revision)) {
        setData((previous) => previous?.owner === owner && value.data.revision >= previous.value.demo_revision
          ? { owner, value: { ...previous.value, demo_balance_minor: value.data.balance_minor, demo_revision: value.data.revision } } : previous);
      }
      return value;
    });
    return () => { axios.interceptors.request.eject(request); axios.interceptors.response.eject(response); };
  }, []);
  useEffect(() => {
    setData(null); locks.current.clear(); setBlocked(false); setError(''); refresh();
    const timer = setInterval(() => { if (!document.hidden && !pending.current) refresh(); }, 5000);
    window.addEventListener('focus', refresh); window.addEventListener('gv-wallet-updated', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('gv-wallet-updated', refresh); invalidateRefresh(); };
  }, [refresh, invalidateRefresh]);
  const act = async (kind, next) => {
    if (!user || pending.current) return;
    if (locks.current.size || state?.active_round) { toast.error('Finish your active round before switching or resetting balances.'); return; }
    pending.current = true; setChanging(true); const owner = user.id;
    try {
      const value = kind === 'switch'
        ? await axios.put(`${API}/account/currency`, { currency: next }, { headers: headers() })
        : await axios.post(`${API}/account/demo/reset`, { request_id: crypto.randomUUID(), revision: state.demo_revision }, { headers: headers() });
      if (current.current.owner === owner) { setData({ owner, value: value.data }); setError(''); window.dispatchEvent(new Event('gv-demo-wallet-updated')); }
    } catch (failure) { toast.error(typeof failure.response?.data?.detail === 'string' ? failure.response.data.detail : 'Unable to update currency. Please retry.'); refresh(); }
    finally { pending.current = false; setChanging(false); }
  };
  return <CurrencyContext.Provider value={{ currency, accountDemo: !!user && currency === 'demo', signedIn: !!user, ready: !user || !!state,
    state, error, busy: changing || blocked, refresh, registerBusy, change: (value) => act('switch', value), resetDemo: () => act('reset') }}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);
export const useCurrencyBusy = (owner, busy) => {
  const { registerBusy } = useCurrency();
  useEffect(() => { registerBusy(owner, busy); return () => registerBusy(owner, false); }, [owner, busy, registerBusy]);
};